import { useCallback, useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { updateProjectStatus as updateStatusWriter } from '../writers/update-project-status';
import { reportError } from '../../lib/monitoring';
import type { ProjectRow, ProjectStatus } from '../database.types';

/**
 * One Project, loaded by id.
 *
 * Authorization is RLS, not a client check: the SELECT policy from 0001 only
 * matches rows the caller owns, so another user's Project returns NO ROWS and
 * is reported as not-found. Crewlink never says "that exists but is not yours",
 * because that sentence is itself a disclosure.
 *
 * There is deliberately no admin or bypass read anywhere in this file.
 */

export type ProjectStatusState = 'loading' | 'ready' | 'not-found' | 'failed';

export interface ProjectState {
  status: ProjectStatusState;
  project: ProjectRow | null;
  /** Message for a load failure. Status-change failures are reported separately. */
  error: string | null;
  reload: () => Promise<void>;
  /**
   * Changes the status through the protected writer, then reconciles with
   * server truth. Resolves to the confirmed row, or throws — nothing is
   * displayed as changed until the server says so.
   */
  changeStatus: (next: ProjectStatus) => Promise<ProjectRow>;
}

type FetchResult = { kind: 'ready'; row: ProjectRow } | { kind: 'not-found' } | { kind: 'failed' };

export function useProject(projectId: string | undefined): ProjectState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<ProjectStatusState>(client ? 'loading' : 'failed');
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  /** Pure fetch. Sets no state, so the effect decides when to apply it. */
  const fetchProject = useCallback(async (): Promise<FetchResult> => {
    if (!client || !projectId) return { kind: 'not-found' };

    // maybeSingle(): zero rows is an ordinary outcome here, not an error — it
    // is what RLS returns for someone else's Project as well as for one that
    // does not exist, and those two must look identical.
    const { data, error: queryError } = await client
      .from('project')
      .select('*')
      .eq('id', projectId)
      .maybeSingle();

    if (queryError) {
      reportError(queryError, 'project.load');
      return { kind: 'failed' };
    }
    return data ? { kind: 'ready', row: data } : { kind: 'not-found' };
  }, [client, projectId]);

  const applyResult = useCallback((result: FetchResult) => {
    if (result.kind === 'failed') {
      setError('Could not load this Project. Check your connection and try again.');
      setStatus('failed');
      return;
    }
    if (result.kind === 'not-found') {
      setProject(null);
      setError(null);
      setStatus('not-found');
      return;
    }
    setProject(result.row);
    setError(null);
    setStatus('ready');
  }, []);

  useEffect(() => {
    // State is applied from the promise callback rather than synchronously in
    // the effect body, and `cancelled` stops a late response from updating an
    // unmounted screen or a screen that has moved to another Project.
    let cancelled = false;
    void fetchProject().then((result) => {
      if (!cancelled) applyResult(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchProject, applyResult]);

  const reload = useCallback(async () => {
    applyResult(await fetchProject());
  }, [fetchProject, applyResult]);

  const changeStatus = useCallback<ProjectState['changeStatus']>(
    async (next) => {
      if (!client) throw new Error('Crewlink is not connected to a server yet.');
      if (!project) throw new Error('project not found');

      try {
        // The status currently on screen is what the guard is checked against.
        const row = await updateStatusWriter(client, project.id, project.status, next);
        // Reconcile with the server rather than trusting the returned row alone.
        applyResult(await fetchProject());
        return row;
      } catch (caught) {
        // A refused stale write means the screen is out of date, so re-read
        // before surfacing the error — Brandon should be looking at the real
        // current status while he reads why his tap did not take effect.
        applyResult(await fetchProject());
        throw caught;
      }
    },
    [client, project, fetchProject, applyResult],
  );

  return { status, project, error, reload, changeStatus };
}
